/**
 * Where the API keys typed in Settings are kept. On the website, in this tab's session only:
 * they go when the tab closes. In the desktop app, on this computer, encrypted by Windows for
 * the Windows account (desktop/keys.cjs), so they are there each time the app opens. The
 * session holds them too, so the app reads them the same way in both.
 */
import type { ApiKeys } from '../types';
import { desktopApp } from './desktopFolder';

export type ApiKeyName = keyof ApiKeys;

export const API_KEY_STORAGE: Record<ApiKeyName, string> = {
    googleGenAi: 'user_google_api_key',
    openAi: 'user_openai_api_key',
    venice: 'user_venice_api_key',
    vertexApiKey: 'user_vertex_api_key',
    vertexProjectId: 'user_vertex_project',
    vertexLocation: 'user_vertex_location',
};
const NAMES = Object.keys(API_KEY_STORAGE) as ApiKeyName[];

const sessionGet = (key: string) => { try { return sessionStorage.getItem(key); } catch { return null; } };
const sessionSet = (key: string, value: string) => { try { sessionStorage.setItem(key, value); } catch { /* full or absent */ } };
const sessionRemove = (key: string) => { try { sessionStorage.removeItem(key); } catch { /* absent */ } };

/**
 * The keys kept: those of this session, with those saved on this computer in the desktop app.
 * `lasting` says whether keys saved here are there after the app closes.
 */
export function loadApiKeys(): { keys: Partial<Record<ApiKeyName, string>>; lasting: boolean } {
    const found: Partial<Record<ApiKeyName, string>> = {};
    for (const name of NAMES) {
        const value = sessionGet(API_KEY_STORAGE[name]);
        if (value) found[name] = value;
    }
    let lasting = false;
    const keys = desktopApp()?.keys;
    if (keys) {
        try {
            const saved = keys.load();
            lasting = !!saved.available;
            for (const name of NAMES) {
                const value = saved.keys?.[name];
                if (typeof value === 'string' && value) {
                    found[name] = value;
                    sessionSet(API_KEY_STORAGE[name], value);
                }
            }
        } catch { /* an older shell or a broken bridge: the session's keys */ }
    }
    return { keys: found, lasting };
}

/**
 * Keeps one key. Says where: 'computer' when the desktop app saved it for later runs, else
 * 'session' (the website, or a desktop app that could not save it, with `problem` saying why).
 */
export async function saveApiKey(name: ApiKeyName, value: string): Promise<{ kept: 'computer' | 'session'; problem?: string }> {
    sessionSet(API_KEY_STORAGE[name], value);
    const keys = desktopApp()?.keys;
    if (!keys) return { kept: 'session' };
    try {
        const result = await keys.save(name, value);
        return result.ok ? { kept: 'computer' } : { kept: 'session', problem: result.message };
    } catch (error) {
        return { kept: 'session', problem: error instanceof Error ? error.message : String(error) };
    }
}

/** Forgets one key, here and on this computer */
export async function removeApiKey(name: ApiKeyName): Promise<void> {
    sessionRemove(API_KEY_STORAGE[name]);
    const keys = desktopApp()?.keys;
    if (!keys) return;
    try { await keys.remove(name); } catch { /* the session's copy is gone; the next save writes over the rest */ }
}
